<template>
  <div>
    <nav class="navbar navbar-top navbar-expand-md bg-white shadow-sm border-bottom" id="navbar-main">
      <div class="container-fluid">
        <div class="d-flex align-items-center">
          <router-link :to="{ name: 'siteManagement' }" class="btn btn-sm btn-outline-secondary mr-3">
            <i class="fas fa-arrow-left mr-1"></i> Back
          </router-link>
          <div>
            <p class="text-uppercase text-muted mb-0 super-page-kicker">Site Management</p>
            <h2 class="h3 mb-0">{{ siteName || 'Edit Site' }}</h2>
          </div>
        </div>
        <user-data></user-data>
      </div>
    </nav>

    <div class="container-fluid mt-0 pt-5 pt-md-8">

      <div v-if="fetchLoading" class="text-center py-6">
        <b-spinner variant="primary" class="mb-3"></b-spinner>
        <p class="text-muted mb-0">Loading site...</p>
      </div>

      <div v-else-if="!siteForEdit && !formReady" class="card shadow-sm border-0">
        <div class="card-body text-center py-6">
          <h5 class="mb-2">Site not found</h5>
          <p class="text-muted mb-3">The site could not be loaded. Try again from Site Management.</p>
          <router-link :to="{ name: 'siteManagement' }" class="btn btn-primary">
            Go to Site Management
          </router-link>
        </div>
      </div>

      <template v-else-if="formReady">
        <!-- Site Identity -->
        <div class="card shadow-sm border-0 mb-4">
          <div class="card-header bg-white border-0 py-3">
            <h3 class="mb-0">Site Details</h3>
          </div>
          <div class="card-body">
            <div class="row">
              <b-form-group label="Site Name" label-for="siteName" class="col-md-4">
                <b-form-input id="siteName" v-model="form.name" placeholder="Short name"></b-form-input>
              </b-form-group>
              <b-form-group label="Site Full Name" label-for="siteFullName" class="col-md-4">
                <b-form-input id="siteFullName" v-model="form.fullname" placeholder="Full name"></b-form-input>
              </b-form-group>
              <b-form-group label="Service Provider" class="col-md-4">
                <b-form-select v-model="form.service_provider_id" class="form-control custom-select">
                  <option :value="null" disabled>Select service provider</option>
                  <option v-for="sp in serviceProviders" :key="sp.id" :value="sp.id">{{ sp.name }}</option>
                </b-form-select>
              </b-form-group>
            </div>
          </div>
        </div>

        <!-- Water -->
        <div v-if="form.water.status" class="card shadow-sm border-0 mb-4">
          <div class="card-header bg-white border-0 py-3">
            <h3 class="mb-0">Water Service</h3>
          </div>
          <div class="card-body">
            <div class="row">
              <b-form-group label="Description" label-for="waterDescription" class="col-md-12">
                <b-form-textarea id="waterDescription" v-model="form.water.description"
                  placeholder="Water description"></b-form-textarea>
              </b-form-group>
              <b-form-group label="Water Rate" class="col-md-4">
                <b-form-select v-model="form.water.cwa_rate_management_id" class="form-control custom-select">
                  <option :value="null" disabled>Select Water Rate</option>
                  <option v-for="rate in rateList.data" :key="rate.id" :value="rate.id">{{ rate.name }}</option>
                </b-form-select>
              </b-form-group>
              <b-form-group label="WMA Rate" class="col-md-4">
                <b-form-select v-model="form.water.wma_rate_management_id" class="form-control custom-select">
                  <option :value="null" disabled>Select WMA Rate</option>
                  <option v-for="rate in rateList.data" :key="rate.id" :value="rate.id">{{ rate.name }}</option>
                </b-form-select>
              </b-form-group>
              <b-form-group label="Payment Terms (In Days)" class="col-md-4">
                <b-form-input v-model="form.water.payment_terms" placeholder="e.g. 30"></b-form-input>
              </b-form-group>
              <b-form-group label="Service Charge" class="col-md-4">
                <b-form-input v-model="form.water.service_charge" placeholder="If billable"></b-form-input>
              </b-form-group>
              <b-form-group label="Meter Rental" class="col-md-4">
                <b-form-input v-model="form.water.meter_rental" placeholder="If billable"></b-form-input>
              </b-form-group>
              <b-form-group label="Custom Message" class="col-md-12">
                <b-form-textarea v-model="form.water.custom_message" placeholder="Printed on bill"></b-form-textarea>
              </b-form-group>
            </div>
          </div>
        </div>

        <!-- Electricity -->
        <div v-if="form.electricity.status" class="card shadow-sm border-0 mb-4">
          <div class="card-header bg-white border-0 py-3">
            <h3 class="mb-0">Electricity Service</h3>
          </div>
          <div class="card-body">
            <div class="row">
              <b-form-group label="Description" class="col-md-12">
                <b-form-textarea v-model="form.electricity.description"
                  placeholder="Electricity description"></b-form-textarea>
              </b-form-group>
              <b-form-group label="Rate" class="col-md-4">
                <b-form-select v-model="form.electricity.rate_management_id" class="form-control custom-select">
                  <option :value="null" disabled>Select rate</option>
                  <option v-for="rate in rateList.data" :key="rate.id" :value="rate.id">{{ rate.name }}</option>
                </b-form-select>
              </b-form-group>
              <b-form-group label="Payment Terms (In Days)" class="col-md-4">
                <b-form-input v-model="form.electricity.payment_terms" placeholder="e.g. 30"></b-form-input>
              </b-form-group>
              <b-form-group label="Service Charge" class="col-md-4">
                <b-form-input v-model="form.electricity.service_charge" placeholder="If billable"></b-form-input>
              </b-form-group>
              <b-form-group label="Meter Rental" class="col-md-4">
                <b-form-input v-model="form.electricity.meter_rental" placeholder="If billable"></b-form-input>
              </b-form-group>
              <b-form-group label="Custom Message" class="col-md-12">
                <b-form-textarea v-model="form.electricity.custom_message"
                  placeholder="Printed on bill"></b-form-textarea>
              </b-form-group>
            </div>
          </div>
        </div>

        <!-- Gas -->
        <div v-if="form.gas.status" class="card shadow-sm border-0 mb-4">
          <div class="card-header bg-white border-0 py-3">
            <h3 class="mb-0">Gas Service</h3>
          </div>
          <div class="card-body">
            <div class="row">
              <b-form-group label="Description" class="col-md-12">
                <b-form-textarea v-model="form.gas.description" placeholder="Gas description"></b-form-textarea>
              </b-form-group>
              <b-form-group label="Rate" class="col-md-4">
                <b-form-select v-model="form.gas.rate_management_id" class="form-control custom-select">
                  <option :value="null" disabled>Select rate</option>
                  <option v-for="rate in rateList.data" :key="rate.id" :value="rate.id">{{ rate.name }}</option>
                </b-form-select>
              </b-form-group>
              <b-form-group label="Payment Terms (In Days)" class="col-md-4">
                <b-form-input v-model="form.gas.payment_terms" placeholder="e.g. 30"></b-form-input>
              </b-form-group>
              <b-form-group label="Service Charge" class="col-md-4">
                <b-form-input v-model="form.gas.service_charge" placeholder="If billable"></b-form-input>
              </b-form-group>
              <b-form-group label="Meter Rental" class="col-md-4">
                <b-form-input v-model="form.gas.meter_rental" placeholder="If billable"></b-form-input>
              </b-form-group>
              <b-form-group label="Custom Message" class="col-md-12">
                <b-form-textarea v-model="form.gas.custom_message" placeholder="Printed on bill"></b-form-textarea>
              </b-form-group>
            </div>
          </div>
        </div>

        <!-- Tax -->
        <div class="card shadow-sm border-0 mb-4">
          <div class="card-header bg-white border-0 py-3">
            <h3 class="mb-0">Tax</h3>
          </div>
          <div class="card-body">
            <div class="row">
              <b-form-group label="Tax Label" label-for="editTaxLabel" class="col-md-4"
                description="e.g. VAT, GST, TVA — leave blank for no tax">
                <b-form-input id="editTaxLabel" v-model.trim="taxForm.tax_label"
                  placeholder="e.g. VAT"></b-form-input>
              </b-form-group>
              <b-form-group label="Rate (%)" label-for="editTaxPct" class="col-md-4"
                description="Applied to the pre-tax subtotal">
                <b-form-input id="editTaxPct" type="number" min="0" max="100" step="0.01"
                  v-model.number="taxForm.tax_percentage" placeholder="e.g. 15"></b-form-input>
              </b-form-group>
            </div>
          </div>
        </div>

        <!-- Action bar -->
        <div class="card shadow-sm border-0 mb-5">
          <div class="card-body d-flex justify-content-between align-items-center">
            <b-button variant="warning" @click="changeServiceProvider" :disabled="loading">
              Change Service Provider
            </b-button>
            <div>
              <router-link :to="{ name: 'siteManagement' }" class="btn btn-link text-muted mr-3">
                Cancel
              </router-link>
              <b-button variant="success" @click="updateSite" :disabled="loading">
                <b-spinner v-if="loading" small class="mr-1"></b-spinner>
                {{ loading ? 'Saving…' : 'Save Changes' }}
              </b-button>
            </div>
          </div>
        </div>
      </template>
    </div>
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';
import userData from '@/pages/Common/userData';

export default {
  // Edit view for a single site/service — loaded from siteManagement list row via Vuex siteForEdit.
  components: { userData },
  data() {
    return {
      loading: false,
      fetchLoading: false,
      formReady: false,
      serviceProviders: [],
      siteName: '',
      taxForm: { tax_label: '', tax_percentage: 0 },
      form: {
        name: '',
        fullname: '',
        service_provider_id: null,
        water: { status: false },
        electricity: { status: false },
        gas: { status: false }
      }
    }
  },
  computed: {
    ...mapGetters('siteManagement', ['siteForEdit', 'serviceProviderList', 'rateList'])
  },
  methods: {
    ...mapActions('siteManagement', [
      'FETCH_SERVICE_PROVIDER',
      'FETCH_RATES',
      'UPDATE_SITE',
      'CHANGE_SERVICE_PROVIDER',
      'FETCH_SITE_FOR_EDIT'
    ]),
    populateForm(data) {
      this.siteName = data.site_mapping ? data.site_mapping.name : ''
      this.form = {
        id: data.site_id,
        name: data.site_mapping.name,
        fullname: data.site_mapping.fullname,
        service_provider_id: data.site_mapping.service_provider_id
      }

      let svc = null
      this.form.water = data.service_type.id === 1
        ? {
          status: true,
          id: data.water_service.id,
          custom_message: data.water_service.custom_message,
          description: data.water_service.description,
          cwa_rate_management_id: data.water_service.cwa_rate_management_id,
          wma_rate_management_id: data.water_service.wma_rate_management_id,
          service_charge: data.water_service.service_charge,
          meter_rental: data.water_service.meter_rental,
          payment_terms: data.water_service.payment_terms
        }
        : { status: false }
      if (data.service_type.id === 1) svc = data.water_service

      this.form.electricity = data.service_type.id === 2
        ? {
          status: true,
          id: data.electricity_service.id,
          rate_management_id: data.electricity_service.rate_management_id,
          description: data.electricity_service.description,
          service_charge: data.electricity_service.service_charge,
          meter_rental: data.electricity_service.meter_rental,
          custom_message: data.electricity_service.custom_message,
          payment_terms: data.electricity_service.payment_terms,
          load_based: data.electricity_service.load_based
        }
        : { status: false }
      if (data.service_type.id === 2) svc = data.electricity_service

      this.form.gas = data.service_type.id === 3
        ? {
          status: true,
          id: data.gas_service.id,
          rate_management_id: data.gas_service.rate_management_id,
          description: data.gas_service.description,
          service_charge: data.gas_service.service_charge,
          meter_rental: data.gas_service.meter_rental,
          payment_terms: data.gas_service.payment_terms,
          custom_message: data.gas_service.custom_message
        }
        : { status: false }
      if (data.service_type.id === 3) svc = data.gas_service

      this.taxForm = {
        tax_label: svc ? (svc.tax_label || '') : '',
        tax_percentage: svc ? Number(svc.tax_percentage || 0) : 0
      }
      this.formReady = true
    },
    populateFormFromEdit(data) {
      this.siteName = data.name || ''
      this.form = {
        id: data.id,
        name: data.name,
        fullname: data.fullname,
        service_provider_id: data.service_provider_id,
        water: data.water || { status: false },
        electricity: data.electricity || { status: false },
        gas: data.gas || { status: false }
      }
      const svc = (data.water && data.water.status) ? data.water
        : (data.electricity && data.electricity.status) ? data.electricity
        : (data.gas && data.gas.status) ? data.gas
        : null
      this.taxForm = {
        tax_label: svc ? (svc.tax_label || '') : '',
        tax_percentage: svc ? Number(svc.tax_percentage || 0) : 0
      }
      this.formReady = true
    },
    updateSite() {
      this.loading = true
      const payload = { ...this.form }
      const tax = {
        tax_label: this.taxForm.tax_label || null,
        tax_percentage: Number(this.taxForm.tax_percentage || 0)
      }
      if (payload.water && payload.water.status) {
        payload.water = { ...payload.water, ...tax }
      } else if (payload.electricity && payload.electricity.status) {
        payload.electricity = { ...payload.electricity, ...tax }
      } else if (payload.gas && payload.gas.status) {
        payload.gas = { ...payload.gas, ...tax }
      }

      this.UPDATE_SITE(payload)
        .then(() => {
          this.$router.replace({ name: 'siteManagement' })
        })
        .catch(() => {})
        .finally(() => {
          this.loading = false
        })
    },
    changeServiceProvider() {
      this.loading = true
      this.CHANGE_SERVICE_PROVIDER({
        site_id: this.form.id,
        service_provider_id: this.form.service_provider_id
      })
        .then((res) => {
          this.SUCCESS_TOAST_MSG(res.message || 'Service provider updated successfully')
          this.$router.replace({ name: 'siteManagement' })
        })
        .catch((err) => {
          this.ERROR_TOAST_MSG(err.message || 'Failed to update service provider')
        })
        .finally(() => {
          this.loading = false
        })
    }
  },
  created() {
    if (this.siteForEdit) {
      this.populateForm(this.siteForEdit)
    } else {
      this.fetchLoading = true
      this.FETCH_SITE_FOR_EDIT({
        site_id: this.$route.params.siteid,
        service_provider_id: this.$route.params.serviceproviderid,
        service_type_id: this.$route.params.servicetypeid
      }).then(data => {
        if (data) this.populateFormFromEdit(data)
      }).catch(() => {}).finally(() => {
        this.fetchLoading = false
      })
    }
    this.FETCH_SERVICE_PROVIDER().then(() => {
      this.serviceProviders = this.serviceProviderList.data || []
    }).catch(() => {})
    this.FETCH_RATES().catch(() => {})
  }
}
</script>

<style scoped>
.super-page-kicker {
  font-size: 0.75rem;
  font-weight: 700;
  letter-spacing: 0.12em;
  color: #5e72e4;
}
</style>



// WEBPACK FOOTER //
// src/pages/SuperAdmin/siteEdit.vue